import {AutocompleteService} from '@frontapp/editor-html';
import React, {FC, useRef} from 'react';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {
  partialVariableRegexp,
  staticVariables,
} from '../../../../services/variableInterpolation/variableInterpolator';
import {useComposerCustomFieldVariables} from '../../../inboxes/composer/settings/useSettingsComposerCustomFieldVariables';
import {ServiceSubscription} from '../serviceSubscription';

/*
 * Constants.
 */

const endToken = '}}';

/*
 * Props.
 */

export interface VariableCompletionDropdownProps {
  autocompleteService: AutocompleteService;
  onVariableInserted?: HandlerOf<string>;
  onShown?: Handler;
}

/*
 * Component.
 */

export const VariableCompletionDropdown: FC<VariableCompletionDropdownProps> = (props) => {
  const {autocompleteService} = props;

  const customFieldVariables = useComposerCustomFieldVariables();
  const isShown = useRef(false);

  return (
    <ServiceSubscription source={autocompleteService}>
      {({textBeforeCursor}) => {
        const render = maybeRenderVariableCompletionEditor(textBeforeCursor, customFieldVariables, props);

        const newIsShown = Boolean(render);
        if (newIsShown !== isShown.current) {
          isShown.current = newIsShown;
          if (newIsShown) {
            props.onShown?.();
          }
        }

        return render;
      }}
    </ServiceSubscription>
  );
};

function maybeRenderVariableCompletionEditor(
  textBeforeCursor: string,
  customFieldVariables: ReadonlyArray<string>,
  props: VariableCompletionDropdownProps,
) {
  const {autocompleteService, onVariableInserted} = props;

  const match = partialVariableRegexp.exec(textBeforeCursor);

  if (!match) {
    return null;
  }

  const token = match[0];
  const maybeVariableName = match[1];

  if (token.endsWith(endToken)) {
    return null;
  }

  const allVariables = [...staticVariables, ...(customFieldVariables ?? [])];

  const matchingVariables = allVariables
    .filter((name) => name.includes(maybeVariableName))
    .sort((a, b) => a.localeCompare(b));

  if (matchingVariables.length < 1) {
    return null;
  }

  const anchor = autocompleteService.getBoundingClientRect(token);

  if (!anchor) {
    return null;
  }

  return (
    <SimpleDropdown
      anchor={() => anchor}
      maxWidth={300}
      maxHeight={350}
      shouldSelectFirst
      shouldSelectWithTab
    >
      {matchingVariables.map((variable) => (
        <SimpleDropdownItem
          key={variable}
          onSelect={() => {
            autocompleteService.autocomplete(token, `{{${variable}}}`);
            onVariableInserted?.(variable);
          }}
        >
          {`{{${variable}}}`}
        </SimpleDropdownItem>
      ))}
    </SimpleDropdown>
  );
}
